import React from 'react';
import { Box, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DashboardCard, DashboardCardSkeleton } from '../../../../../shared/components';
import { GaugeChart } from '../../../../../shared/components/charts';
import { AnnotatableChart } from '../../../../../shared/components/charts/AnnotatableChart';
import { roundToDecimal } from '../../shared/utils';
import type { MetricsFilters } from '../../../../../shared/api/types';
import type { HrvTrendPoint } from '../api/types';

interface HrvGaugeProps {
  stats: { average_hrv: number | null; resting_heart_rate: number | null } | undefined;
  isLoading: boolean;
  showBreakdown?: boolean;
  breakdownOpen?: boolean;
  onToggleBreakdown?: () => void;
}

export const HrvGauge: React.FC<HrvGaugeProps> = ({
  stats,
  isLoading,
  showBreakdown,
  breakdownOpen,
  onToggleBreakdown,
}) => {
  if (isLoading) {
    return <DashboardCardSkeleton height={200} />;
  }

  const hrvValue = stats?.average_hrv != null ? roundToDecimal(stats.average_hrv) : null;
  const hrvBaseline = { min: 45, max: 60 };

  return (
    <DashboardCard
      title='Average HRV'
      subtitle='Gauge shows how current HRV compares to baseline range.'
      expandable
      expandedContent={
        <GaugeChart
          value={hrvValue}
          min={0}
          max={120}
          baseline={hrvBaseline}
          unit='ms'
          title='Average HRV'
          useNeutralColors
          height={400}
        />
      }
    >
      <GaugeChart
        value={hrvValue}
        min={0}
        max={120}
        baseline={hrvBaseline}
        unit='ms'
        title='Average HRV'
        useNeutralColors
        height={200}
      />
      {showBreakdown && onToggleBreakdown && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', px: 2, pb: 1 }}>
          <Button
            size='small'
            variant='text'
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: breakdownOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s',
                }}
              />
            }
            onClick={onToggleBreakdown}
            sx={{ fontSize: '0.7rem', textTransform: 'none', color: 'text.secondary' }}
          >
            {breakdownOpen ? 'Hide breakdown' : 'Breakdown'}
          </Button>
        </Box>
      )}
    </DashboardCard>
  );
};

interface HrvTrendChartProps {
  deptName: string;
  stats: { average_hrv: number | null; resting_heart_rate: number | null } | undefined;
  trends: HrvTrendPoint[] | undefined;
  isLoading: boolean;
  clientId: string | null;
  weatherFilters: MetricsFilters;
}

export const HrvTrendChart: React.FC<HrvTrendChartProps> = ({
  deptName,
  stats,
  trends,
  isLoading,
  clientId,
  weatherFilters,
}) => {
  if (isLoading) {
    return <DashboardCardSkeleton height={250} />;
  }

  const trendData =
    trends?.map((t) => ({
      label: t.period_start,
      recovery_score: t.recovery_score != null ? roundToDecimal(t.recovery_score) : null,
      strain: t.strain != null ? roundToDecimal(t.strain) : null,
    })) ?? [];

  return (
    <DashboardCard
      title='Strain and Recovery Trend'
      subtitle={`${deptName} strain and recovery over time`}
      expandable
      expandedContent={
        <AnnotatableChart
          clientId={clientId}
          startDate={weatherFilters.start_date}
          endDate={weatherFilters.end_date}
          weatherFilters={weatherFilters}
          data={trendData}
          xAxisKey='label'
          series={[
            { dataKey: 'recovery_score', label: 'Recovery', color: '#4caf50', area: true },
            { dataKey: 'strain', label: 'Strain', color: '#f44336', area: true },
          ]}
          yAxisMin={0}
          yAxisMax={100}
          height={500}
        />
      }
    >
      <AnnotatableChart
        clientId={clientId}
        startDate={weatherFilters.start_date}
        endDate={weatherFilters.end_date}
        weatherFilters={weatherFilters}
        data={trendData}
        xAxisKey='label'
        series={[
          { dataKey: 'recovery_score', label: 'Recovery', color: '#4caf50', area: true },
          { dataKey: 'strain', label: 'Strain', color: '#f44336', area: true },
        ]}
        yAxisMin={0}
        yAxisMax={100}
        height={250}
      />
    </DashboardCard>
  );
};
